import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [track, setTrack] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [previewMode, setPreviewMode] = useState(true);

  useEffect(() => {
    audioRef.current = new Audio();
    const a = audioRef.current;
    const onTime = () => {
      setProgress(a.currentTime);
      if (previewMode && a.currentTime >= 30) {
        a.pause();
        setPlaying(false);
      }
    };
    const onLoaded = () => setDuration(a.duration);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("ended", onEnd);
    return () => {
      a.pause();
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("ended", onEnd);
    };
  }, [previewMode]);

  const playTrack = useCallback((t, { preview = true } = {}) => {
    const a = audioRef.current;
    if (!a) return;
    setPreviewMode(preview);
    if (track?.id === t.id) {
      if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
      return;
    }
    setTrack(t);
    a.src = t.audio_url;
    a.currentTime = 0;
    a.play().then(() => setPlaying(true)).catch((err) => {
      console.error("Audio play failed:", err);
      setPlaying(false);
    });
  }, [track]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a || !track) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  }, [track]);

  const seek = useCallback((pct) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const target = pct * (previewMode ? Math.min(30, duration) : duration);
    a.currentTime = target;
  }, [duration, previewMode]);

  const value = useMemo(
    () => ({ track, playing, progress, duration, previewMode, playTrack, toggle, seek }),
    [track, playing, progress, duration, previewMode, playTrack, toggle, seek]
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export const usePlayer = () => useContext(PlayerContext);
